import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { lazy, Suspense } from 'react';
import { CommunityFeedSkeleton } from '@/components/community/CommunitySkeletons';
import { requireAlfieCapability } from '@/api/alfie-plan';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { chatSearchFrom } from '@/lib/chat-link';
import { communitySearchSchema } from '@/lib/community-search';

const Community = lazy(() => import('@/pages/Community'));

export type CommunitySearchParams = z.infer<typeof communitySearchSchema>;

export const Route = createFileRoute('/community/')({
  // Community access comes with a paid Alfie plan — Free goes to upgrade.
  // With Speedies on, chat lives at /chat: an old `tab=chats` link, or a link carrying a
  // conversation or message with no tab (notification URLs, stored ones included), lands there
  // with every chat key kept.
  beforeLoad: async ({ context: { queryClient }, search }) => {
    await requireAlfieCapability(queryClient, 'community');
    if (!isSpeediesEnabled()) return;
    const chatBound = search.tab === 'chats' ||
      (!search.tab && !!(search.conversation || search.message || search.filter));
    if (chatBound) throw redirect({ to: '/chat', search: chatSearchFrom(search), replace: true });
  },
  // Explicit param + return types: with beforeLoad present, an untyped
  // `(search) => schema.parse(search)` collapses the inferred search type to {}.
  validateSearch: (search: Record<string, unknown>): CommunitySearchParams =>
    communitySearchSchema.parse(search),
  // `beforeLoad` runs BEFORE the component exists, and TanStack keeps the
  // previous route on screen until it resolves — so a Suspense fallback alone
  // can't cover it, and without this the old page just froze. Same skeleton for
  // both phases (gate, then chunk) so the wait reads as one loading state.
  pendingComponent: CommunityFeedSkeleton,
  component: () => (
    <Suspense fallback={<CommunityFeedSkeleton />}>
      <Community />
    </Suspense>
  ),
});
